import type { FieldConfiguration } from "@/types/workflow"

type RetainedColumn = Pick<FieldConfiguration, "columnCode" | "isCustomField" | "isRetainDataEnabled">

/**
 * The row an expanded child WorkOrder renders: a copy of `child` whose retained custom-field columns
 * (`isCustomField && isRetainDataEnabled`) carry the parent assignment row's value. Retained values belong
 * to the assignment and live on its row, so any WorkOrder's own copy can be stale. Keys are matched
 * case-insensitively, as GridCell reads a row, and a value the parent lacks is left out so the cell renders
 * empty like the parent's. `child` is not modified.
 */
export function mergeRetainedFromParent<T extends Record<string, any>>(
  child: T,
  parent: Record<string, any>,
  columns: RetainedColumn[],
): T {
  const retainedCodes = new Set(
    columns
      .filter((column) => column.isCustomField && column.isRetainDataEnabled && column.columnCode)
      .map((column) => column.columnCode.toLowerCase()),
  )
  if (retainedCodes.size === 0) return child

  const parentKeyByCode = new Map<string, string>()
  for (const key of Object.keys(parent)) {
    const code = key.toLowerCase()
    if (retainedCodes.has(code) && !parentKeyByCode.has(code)) parentKeyByCode.set(code, key)
  }

  const merged: Record<string, any> = {}
  for (const key of Object.keys(child)) {
    if (!retainedCodes.has(key.toLowerCase())) merged[key] = child[key]
  }
  for (const parentKey of parentKeyByCode.values()) merged[parentKey] = parent[parentKey]
  return merged as T
}
